<template>
  <div>
    <div v-if="showAddStudentLink" class="text-end">
      <a @click="showStudentForm = true">Add New Student</a>
    </div>

    <v-autocomplete
      v-model="selectedStudent"
      :items="students"
      item-text="fullName"
      item-value="id"
      label="Search Students"
      clearable
      return-object
      :rules="[requiredRule]"
      @input="updateValue"
      @update:search-input="debouncedFetchStudents"
      v-bind="$attrs"
    >
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title>
            {{ item.fullName }} {{ formatDate(item.dob) }}
            {{ item.email ? `(${item.email})` : "" }}
            {{
              !globalCenter || !globalCenter.id
                ? `@ ${centerName(item.centerId)}`
                : ""
            }}
          </v-list-item-title>
        </v-list-item-content>
      </template>
    </v-autocomplete>
    <v-dialog v-model="showStudentForm" max-width="600px">
      <student-form
        :centers="myCenters"
        @submit="addNewStudent"
        @close="showStudentForm = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import StudentForm from "@/components/student/NewStudentForm.vue";

export default {
  components: {
    StudentForm,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    centerId: {
      type: String,
      required: false,
      default: () => null,
    },
    activeOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    classroomIds: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    showAddStudentLink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedStudent: this.value,
      students: [],
      requiredRule: (value) => {
        if (this.required) {
          return !!value || "Student selection is required.";
        }
        return true;
      },
      showStudentForm: false,
    };
  },
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
  },
  created() {
    // This creates a debounced version of fetchStudents method.
    // The function will be invoked after 300ms from the last call.
    this.debouncedFetchStudents = _.debounce(this.fetchStudents, 300);

    if (this.selectedStudent && this.selectedStudent.id) {
      // Check if fullName is available, if not, set it by joining first and last name
      if (!this.selectedStudent.fullName) {
        this.selectedStudent.fullName = `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`;
      }
      this.students.push(this.selectedStudent);
    } else {
      this.students = [];
    }
  },
  watch: {
    value(newValue) {
      if (
        newValue &&
        !newValue.fullName &&
        newValue.firstName &&
        newValue.lastName
      ) {
        newValue.fullName = `${newValue.firstName} ${newValue.lastName}`;
      }
      this.selectedStudent = newValue;

      // Check if the new value is already in the students list
      if (
        newValue &&
        !this.students.some((student) => student.id === newValue.id)
      ) {
        this.students.push(newValue);
      }
    },
  },
  methods: {
    ...mapActions(["addErrorMessage"]),
    async addNewStudent(newStudent) {
      this.showStudentForm = false;
      if (newStudent) {
        this.students = [...this.students, newStudent];
      }
    },
    async fetchStudents(searchInput) {
      if (typeof searchInput !== "string" || !searchInput) {
        return;
      }

      const response = await this.$axios.get("/student", {
        params: {
          centerId: this.centerId,
          activeOnly: this.activeOnly,
          classroomIds: this.classroomIds.join(),
          searchTerms: searchInput,
          size: 50,
        },
      });

      this.students = response.data.content;
    },
    updateValue(value) {
      this.$emit("input", value);
    },
    formatDate(dob) {
      if (!dob) {
        return ""; // If dob is null or undefined, return an empty string
      }

      return `(DoB: ${moment(dob).format("MMM DD YYYY")})`;
    },
    centerName(centerId) {
      return this.getCenterName(centerId);
    },
  },
};
</script>
